import { useRef } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useGetCurrentUserQuery } from '@/entities/user'
import { useGetClientCountQuery } from '../api/subscription'
import { Loader } from '@/shared/ui' // or any spinner/loading component you use

export const RequireSubscription = ({
	children
}: {
	children: JSX.Element
}) => {
	const {
		data: currentUser,
		isLoading: isUserLoading,
		isFetching: isUserFetching
	} = useGetCurrentUserQuery(undefined, { refetchOnMountOrArgChange: true })

	const {
		data: clientNumber,
		isLoading: isClientLoading,
		isFetching: isClientNumberFetching
	} = useGetClientCountQuery(
		{
			organizationId: currentUser?.organizationId
		},
		{ refetchOnMountOrArgChange: true }
	)

	const location = useLocation()
	// Once the page is shown, later refetches (e.g. the user refetched after a
	// tax-return upload creates a client draft) must not swap it for the loader
	// — that unmounts the create-client flow mid-way.
	const hasRenderedRef = useRef(false)

	// Guard logic
	const hasActiveSubscription = currentUser?.activeSubscription
	const hasReachedLimit =
		clientNumber?.currentClientNumber >= clientNumber?.maxClientNumber
	// ensures that the user does not get redirected when they skip free trial
	// so by adding this variable, after the user skips the free trial, they will not be redirected
	// and can continue adding clients
	const isOnFreeTrial = currentUser?.freeTrail === true

	if (
		!hasRenderedRef.current &&
		(isUserLoading ||
			isClientLoading ||
			isUserFetching ||
			isClientNumberFetching)
	) {
		return <Loader />
	}

	if (hasActiveSubscription && hasReachedLimit && isOnFreeTrial) {
		return <Navigate to='/' state={{ from: location }} replace />
	}

	// No free-plan check here: the flow's own tax-return upload already uses up
	// the free client (drafts count), so the page must stay open to finish it.
	// The "Add client" entry points and the backend's 400 stop a second client.
	hasRenderedRef.current = true

	return children
}
